import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { IProFormaItem, IProFormaRequest, IRelacionItem, IRelacionRequest, IProFormaAddRequest, IProFormaDeleteRequest } from '../interfaces/sector.interface';
import { SectoresService } from '../services/sectores.service';

@Component({
  selector: 'app-proforma',
  templateUrl: './proforma.component.html',
  providers: [MessageService]
})
export class ProFormaComponent implements OnInit, OnChanges {
  @Input() sector: string = '';
  @Input() descripcion: string = '';
  @Input() visible: boolean = false;

  @ViewChild('dt') table!: Table;
  @ViewChild('dtRelaciones') tableRelaciones!: Table;
  
  dataSource: IProFormaItem[] = [];
  loading: boolean = false;
  first: number = 0;
  rows: number = 10;
  totalRecords: number = 0;

  // Variables para Relaciones
  displayRelacionesDialog: boolean = false;
  relacionesDataSource: IRelacionItem[] = [];
  relacionesLoading: boolean = false;
  relacionesFirst: number = 0;
  relacionesRows: number = 5;
  relacionesTotalRecords: number = 0;

  // Variables para el mini diálogo de secuencia
  displaySecuenciaDialog: boolean = false;
  secuenciaForm!: FormGroup;
  selectedRelacion: IRelacionItem | null = null;

  displayedColumns = [
    { field: 'numero_relacion', header: 'Relación', sortable: true },
    { field: 'secuencia', header: 'Secuencia', sortable: true },
    { field: 'descripcion', header: 'Descripción', sortable: true },
    { field: 'tipo', header: 'Tipo', sortable: true },
    { field: 'actions', header: 'Acciones', sortable: false }
  ];

  relacionesDisplayedColumns = [
    { field: 'numero_relacion', header: 'Número de Relación', sortable: true },
    { field: 'tipo', header: 'Tipo', sortable: true },
    { field: 'descripcion', header: 'Descripción', sortable: true }
  ];

  constructor(
    private messageService: MessageService,
    private sectoresService: SectoresService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.initSecuenciaForm();
    // No cargar datos automáticamente al inicializar
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Solo cargar datos cuando el componente se vuelve visible y tiene un sector válido
    if (changes['visible'] && changes['visible'].currentValue === true && this.sector) {
      this.loadData();
    }
  }

  initSecuenciaForm(): void {
    this.secuenciaForm = this.fb.group({
      secuencia: [0, [Validators.required, Validators.min(0)]]
    });
  }

  loadData(): void {
    if (!this.sector) {
      return;
    }

    this.loading = true;

    const request: IProFormaRequest = {
      sSector: this.sector,
      sTipo: ""
    };

    this.sectoresService.getProForma(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource = response.data;
          this.totalRecords = response.data.length;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los datos de ProForma'
          });
        }
      },
      error: (error) => {
        console.error('ProForma loadData - Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los datos de ProForma'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  loadRelacionesData(): void {
    if (!this.sector) {
      return;
    }

    this.relacionesLoading = true;

    const request: IRelacionRequest = {
      que: 4,
      tope: "999",
      cmd: "",
      numero_relacion: 0,
      tipo: "",
      descripcion: "",
      formula: "",
      periodicidad: "",
      tipoVariable: ""
    };

    this.sectoresService.getRelaciones(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.relacionesDataSource = response.data;
          this.relacionesTotalRecords = response.data.length;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los datos de Relaciones'
          });
        }
      },
      error: (error) => {
        console.error('Relaciones loadData - Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los datos de Relaciones'
        });
      },
      complete: () => {
        this.relacionesLoading = false;
      }
    });
  }

  onPage(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData();
  }

  onRelacionesPage(event: any): void {
    this.relacionesFirst = event.first;
    this.relacionesRows = event.rows;
    this.loadRelacionesData();
  }

  openRelaciones(): void {
    this.displayRelacionesDialog = true;
    this.loadRelacionesData();
  }

  onRelacionesDialogClosed(): void {
    this.displayRelacionesDialog = false;
  }

  openSecuenciaDialog(relacion: IRelacionItem): void {
    this.selectedRelacion = relacion;
    this.secuenciaForm.patchValue({ secuencia: 0 });
    this.displaySecuenciaDialog = true;
  }

  onSecuenciaDialogClosed(): void {
    this.displaySecuenciaDialog = false;
    this.selectedRelacion = null;
  }

  saveSecuencia(): void {
    if (this.secuenciaForm.valid && this.selectedRelacion) {
      const formValue = this.secuenciaForm.getRawValue();
      
      const request: IProFormaAddRequest = {
        que: 1,
        tope: "",
        sector: this.sector,
        numerorelacion: this.selectedRelacion.numero_relacion,
        secuencia: formValue.secuencia,
        tipo: this.selectedRelacion.tipo
      };

      this.sectoresService.addProForma(request).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Secuencia agregada correctamente'
            });
            // Cerrar ambos diálogos
            this.displaySecuenciaDialog = false;
            this.displayRelacionesDialog = false;
            this.selectedRelacion = null;
            // Recargar datos de ProForma
            this.loadData();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al agregar la secuencia'
            });
          }
        },
        error: (error) => {
          console.error('Error al agregar secuencia:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al agregar la secuencia'
          });
        }
      });
    }
  }

  openDelete(item: IProFormaItem): void {
    const request: IProFormaDeleteRequest = {
      que: 3,
      tope: "",
      sector: this.sector,
      numerorelacion: item.numero_relacion,
      secuencia: item.secuencia,
      tipo: item.tipo
    };

    this.sectoresService.deleteProForma(request).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'ProForma eliminada correctamente'
          });
          // Recargar datos de ProForma
          this.loadData();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar la ProForma'
          });
        }
      },
      error: (error) => {
        console.error('Error al eliminar ProForma:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al eliminar la ProForma'
        });
      }
    });
  }
}
